import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, Loader2, KeyRound, Copy, AlertTriangle } from "lucide-react";

export default function PinReveal() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${backend}/api/time/pin-reveal/${token}`);
        const body = await r.json();
        if (!alive) return;
        if (!r.ok) setState({ loading: false, data: null, error: body.detail || t("prv.invalid") });
        else setState({ loading: false, data: body, error: null });
      } catch {
        if (alive) setState({ loading: false, data: null, error: t("prv.invalid") });
      }
    })();
    return () => { alive = false; };
  }, [token, backend, t]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-4 py-10" data-testid="pin-reveal">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-2">
          <span className="font-heading text-xl font-extrabold text-navy">Employ</span>
          <span className="-ml-2 font-heading text-xl font-extrabold text-cyan-dark">Guard</span>
        </div>

        {state.loading ? (
          <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>
        ) : state.error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center" data-testid="pin-reveal-error">
            <AlertTriangle className="mx-auto mb-2 h-7 w-7 text-red-500" />
            <p className="text-sm font-medium text-red-700">{state.error}</p>
          </div>
        ) : (
          <div className="text-center" data-testid="pin-reveal-success">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100"><ShieldCheck className="h-6 w-6 text-emerald-600" /></div>
            <h1 className="font-heading text-xl font-extrabold text-navy">{t("prv.title")}</h1>
            <p className="mt-1 text-sm text-slate-500">{state.data.worker_name}</p>
            <div className="my-6 rounded-2xl border border-slate-200 bg-slate-50 py-6">
              <div className="mb-1 flex items-center justify-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400"><KeyRound className="h-3.5 w-3.5" /> {t("prv.yourPin")}</div>
              <div className="font-mono text-4xl font-extrabold tracking-[0.3em] text-navy" data-testid="pin-reveal-value">{state.data.pin}</div>
            </div>
            <button onClick={() => { navigator.clipboard?.writeText(state.data.pin); toast.success(t("prv.copied")); }}
              className="text-sm font-medium text-cyan-dark hover:underline" data-testid="pin-reveal-copy">
              <Copy className="mr-1 inline h-4 w-4" /> {t("prv.copy")}
            </button>
            <p className="mt-6 text-xs text-slate-400">{t("prv.note")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
